import React, { useRef, useState, KeyboardEvent } from 'react';
import { TEXT } from './Typography';

interface DropzoneProps {
  onFiles: (_files: File[]) => void;
  /** Called when a drop produced no readable file — e.g. dragging an attachment
   *  straight from a desktop email client (Outlook), which hands the browser a
   *  Windows file-promise it can't read. Lets the caller show guidance. */
  onEmptyDrop?: () => void;
  multiple?: boolean;
  accept?: string;
  className?: string;
  emptyText?: string;
  busy?: boolean;
}

export default function Dropzone({
  onFiles,
  onEmptyDrop,
  multiple = true,
  accept = undefined,
  className = '',
  emptyText = 'Drag & drop files here, or click to browse',
  busy = false,
}: DropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  function handleDrop(e: React.DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (busy) return;
    // Prefer dataTransfer.files; fall back to items (some sources only populate
    // one or the other). A drop with neither = an unreadable source (e.g. a
    // desktop-Outlook file-promise) — tell the caller so it can guide the user.
    let files = Array.from(e.dataTransfer?.files || []);
    if (!files.length && e.dataTransfer?.items) {
      files = Array.from(e.dataTransfer.items)
        .filter(it => it.kind === 'file')
        .map(it => it.getAsFile())
        .filter((f): f is File => !!f);
    }
    if (files.length) onFiles?.(multiple ? files : files.slice(0, 1));
    else onEmptyDrop?.();
  }

  function handlePick(e: React.ChangeEvent<HTMLInputElement>): void {
    const _files = Array.from(e.target?.files || []);
    if (_files.length) onFiles?.(multiple ? _files : _files.slice(0, 1));
    // reset so selecting the same file again still triggers change
    e.target.value = '';
  }

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    if ((e.key === 'Enter' || e.key === ' ') && !busy) {
      inputRef.current?.click();
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => !busy && inputRef.current?.click()}
      onKeyDown={handleKeyDown}
      onDragEnter={e => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragOver={e => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={e => {
        e.preventDefault();
        setDragOver(false);
      }}
      onDrop={handleDrop}
      className={[
        'rounded-lg border-2 border-dashed p-6 text-center cursor-pointer bg-white',
        dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:bg-gray-50',
        busy ? 'opacity-60 cursor-not-allowed' : '',
        className,
      ].join(' ')}
      aria-label="File uploader. Drag and drop files or click to browse."
    >
      <div className={TEXT.body}>{emptyText}</div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handlePick}
        hidden
      />
    </div>
  );
}
